import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, IconButton, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { ExpDeductionKind, ICreateExpRealizationRequest, IExpForm, IExpRealizationDeductionRequest, IExpRealizationPreview } from '../../interfaces/commercial.interface';
import { Account } from '../../interfaces/accounting.interface';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { Drawdown, Facility } from '../../interfaces/borrowing.interface';
import { AccountService } from '../../services/account.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { BorrowingService } from '../../services/borrowing.service';
import { ExpFormService } from '../../services/expForm.service';
import { postingAccounts } from '../../utils/accountHierarchy';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney } from '../../utils/format';

const KIND_LABEL: Record<ExpDeductionKind, string> = {
    SOURCE_TAX: 'Source tax (AIT)', LOAN_LIQUIDATION: 'Loan liquidation', ERQ_RETENTION: 'ERQ retention', OTHER: 'Other',
};
const LIQUIDATABLE = ['PACKING_CREDIT', 'EDF', 'LTR', 'PAD', 'TIME_LOAN'];
const num = (v: string): number | null => (v === '' ? null : Number(v));
const cell = { flex: '1 1 150px', minWidth: 140 } as const;

interface Props {
    institutionId: string;
    exp: IExpForm;
    base: ICreateExpRealizationRequest;
    rows: IExpRealizationDeductionRequest[];
    onChange: (rows: IExpRealizationDeductionRequest[]) => void;
    canViewLoans: boolean;
}

/**
 * "Proceeds distribution" (W3-B2): splits the realised amount into source tax, loan liquidations (PC / EDF / PAD),
 * ERQ retention and other deductions, so the bank advice (net to bank) and the journal match. No rows = the usual
 * single-leg posting.
 */
/** A foreign principal only makes sense on a foreign-currency drawdown; a BDT facility row has no such field. */
const isForeignDrawdown = (rows: { d: Drawdown; f: Facility }[], drawdownId?: string | null): boolean => {
    const sel = rows.find(x => x.d.id === drawdownId);
    return !sel || sel.d.foreignAmount != null || (!!sel.f.currencyCode && sel.f.currencyCode !== 'BDT');
};

const ProceedsDistributionGrid: React.FC<Props> = ({ institutionId, exp, base, rows, onChange, canViewLoans }) => {
    const toast = useToast();
    const isBdt = exp.currencyCode === 'BDT';
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [banks, setBanks] = useState<BankAccount[]>([]);
    const [drawdowns, setDrawdowns] = useState<{ d: Drawdown; f: Facility }[]>([]);
    const [preview, setPreview] = useState<IExpRealizationPreview | null>(null);
    const [busy, setBusy] = useState(false);

    const kinds = useMemo<ExpDeductionKind[]>(() => {
        const k: ExpDeductionKind[] = ['SOURCE_TAX'];
        if (canViewLoans) k.push('LOAN_LIQUIDATION');
        if (!isBdt) k.push('ERQ_RETENTION');
        k.push('OTHER');
        return k;
    }, [canViewLoans, isBdt]);

    const needs = (kind: ExpDeductionKind) => rows.some(r => r.kind === kind);
    useEffect(() => {
        if (!needs('OTHER') || accounts.length) return;
        AccountService.getAllAccounts().then(a => setAccounts(postingAccounts(a ?? []))).catch(() => setAccounts([]));
    }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!needs('ERQ_RETENTION') || banks.length) return;
        BankAccountService.listByInstitution(institutionId).then(b => setBanks((b ?? []).filter(x => x.accountType === 'ERQ' || x.accountType === 'FC'))).catch(() => setBanks([]));
    }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!needs('LOAN_LIQUIDATION') || drawdowns.length) return;
        (async () => {
            try {
                const facilities = (await BorrowingService.listFacilities(institutionId)).filter(f => LIQUIDATABLE.includes(f.facilityType));
                const all: { d: Drawdown; f: Facility }[] = [];
                for (const f of facilities) {
                    (await BorrowingService.listDrawdowns(f.id)).filter(d => d.status === 'ACTIVE').forEach(d => all.push({ d, f }));
                }
                setDrawdowns(all);
            } catch (e) {
                toast.showApiError(e, 'Could not load the loan drawdowns');
            }
        })();
    }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

    const update = (i: number, patch: Partial<IExpRealizationDeductionRequest>) => {
        setPreview(null);
        onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    };
    const add = () => { setPreview(null); onChange([...rows, { kind: 'SOURCE_TAX' }]); };
    const remove = (i: number) => { setPreview(null); onChange(rows.filter((_, j) => j !== i)); };

    const runPreview = async () => {
        setBusy(true);
        try {
            setPreview(await ExpFormService.previewRealization(institutionId, exp.id, { ...base, deductions: rows }));
        } catch (e) {
            toast.showApiError(e, 'Could not preview the distribution');
        } finally {
            setBusy(false);
        }
    };

    const grossBase = Math.round((base.amount || 0) * (base.exchangeRate || 0) * 100) / 100;

    return (
        <Box data-testid="proceeds-distribution">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ flex: '1 1 200px' }}>Proceeds distribution</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={add} data-testid="add-deduction" data-help="commercial.realization.addDeduction">Add deduction</Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
                Gross {exp.currencyCode} {formatMoney(base.amount)} x rate {base.exchangeRate} = BDT {formatMoney(grossBase)}. Add one row per
                deduction on the bank advice. No rows posts the full amount to the bank.
            </Typography>
            {rows.length > 0 && (
                <TableContainer data-help-section="commercial.dist.table" sx={{ mt: 1 }}>
                    <Table size="small">
                        <TableHead><TableRow><TableCell data-help="commercial.dist.col.kind">Kind</TableCell><TableCell data-help="commercial.dist.col.details">Details</TableCell><TableCell /></TableRow></TableHead>
                        <TableBody>
                            {rows.map((r, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ verticalAlign: 'top', minWidth: 150 }}>
                                        <TextField data-help="commercial.dist.kindField" select size="small" fullWidth value={r.kind}
                                            onChange={e => update(i, { kind: e.target.value as ExpDeductionKind })}>
                                            {kinds.map(k => <MenuItem key={k} value={k}>{KIND_LABEL[k]}</MenuItem>)}
                                        </TextField>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {(r.kind === 'SOURCE_TAX' || r.kind === 'OTHER') && (
                                                <TextField sx={cell} size="small" type="number" data-help="commercial.dist.amount" label="Amount (BDT)" value={r.amount ?? ''}
                                                    onChange={e => update(i, { amount: num(e.target.value) })} />
                                            )}
                                            {r.kind === 'SOURCE_TAX' && (
                                                <TextField sx={cell} size="small" data-help="commercial.dist.certificate" label="Certificate ref" value={r.certificateRef ?? ''}
                                                    onChange={e => update(i, { certificateRef: e.target.value })} />
                                            )}
                                            {r.kind === 'OTHER' && (
                                                <>
                                                    <Box sx={cell}>
                                                        <SearchableSelect data-help="commercial.dist.debitAccount" options={accounts} value={r.accountId ?? ''} onChange={id => update(i, { accountId: id })}
                                                            getOptionLabel={a => `${a.code} ${a.name}`} label="Debit account" size="small" />
                                                    </Box>
                                                    <TextField data-help="commercial.dist.memo" sx={cell} size="small" label="Memo" required value={r.memo ?? ''}
                                                        onChange={e => update(i, { memo: e.target.value })} />
                                                </>
                                            )}
                                            {r.kind === 'ERQ_RETENTION' && (
                                                <>
                                                    <Box sx={cell}>
                                                        <SearchableSelect data-help="commercial.dist.ercAccount" options={banks} value={r.bankAccountId ?? ''} onChange={id => update(i, { bankAccountId: id })}
                                                            getOptionLabel={b => `${b.bankName} ${b.accountNumber} (${b.accountType})`} label="ERQ / FC account" size="small" />
                                                    </Box>
                                                    <TextField sx={cell} size="small" type="number" data-help="commercial.dist.retained" label={`Retained (${exp.currencyCode})`} value={r.foreignAmount ?? ''}
                                                        onChange={e => update(i, { foreignAmount: num(e.target.value) })} />
                                                </>
                                            )}
                                            {r.kind === 'LOAN_LIQUIDATION' && (
                                                <>
                                                    <Box sx={cell}>
                                                        <SearchableSelect data-help="commercial.dist.drawdown" options={drawdowns} value={r.drawdownId ?? ''} onChange={id => update(i, { drawdownId: id })}
                                                            getOptionId={x => x.d.id}
                                                            getOptionLabel={x => `${x.f.facilityNo} / ${x.d.drawdownNo} (out ${formatMoney(x.d.outstanding)})`}
                                                            label="Loan drawdown" size="small" />
                                                    </Box>
                                                    {!isBdt && isForeignDrawdown(drawdowns, r.drawdownId) && (
                                                        <TextField sx={cell} size="small" type="number" data-help="commercial.dist.principalForeign" label={`Principal (${exp.currencyCode})`} value={r.foreignAmount ?? ''}
                                                            onChange={e => update(i, { foreignAmount: num(e.target.value) })} />
                                                    )}
                                                    <TextField sx={cell} size="small" type="number" data-help="commercial.dist.principalPaid" label="Principal paid (BDT)" value={r.principalAmount ?? ''}
                                                        onChange={e => update(i, { principalAmount: num(e.target.value) })} />
                                                    <TextField sx={cell} size="small" type="number" data-help="commercial.dist.interest" label="Interest (BDT)" value={r.interestAmount ?? ''}
                                                        onChange={e => update(i, { interestAmount: num(e.target.value) })} />
                                                    <TextField sx={cell} size="small" type="number" data-help="commercial.dist.loanCharges" label="Loan charges (BDT)" value={r.chargesAmount ?? ''}
                                                        onChange={e => update(i, { chargesAmount: num(e.target.value) })} />
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                        <IconButton data-help="commercial.dist.remove" size="small" aria-label="Remove deduction" onClick={() => remove(i)}><DeleteIcon fontSize="small" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {rows.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Button size="small" onClick={runPreview} disabled={busy} data-testid="preview-distribution" data-help="commercial.realization.preview">Preview net to bank and journal</Button>
                </Box>
            )}
            {preview && (
                <Box sx={{ mt: 1 }} data-testid="distribution-preview" data-help-section="commercial.dist.preview">
                    <Alert severity="info" sx={{ mb: 1 }}>
                        Gross BDT {formatMoney(preview.grossBase)}, deductions {formatMoney(preview.deductionsTotal)}, bank charges {formatMoney(preview.bankCharges)}:
                        {' '}<strong data-testid="net-to-bank">net to bank BDT {formatMoney(preview.netToBank)}</strong>
                    </Alert>
                    {preview.warnings.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
                    <Table size="small">
                        <TableHead><TableRow><TableCell data-help="commercial.dist.pv.col.line">Journal line</TableCell><TableCell align="right" data-help="commercial.dist.pv.col.debit">Debit</TableCell><TableCell align="right" data-help="commercial.dist.pv.col.credit">Credit</TableCell></TableRow></TableHead>
                        <TableBody>
                            {preview.lines.map((l, i) => (
                                <TableRow key={i}>
                                    <TableCell>{l.label}</TableCell>
                                    <TableCell align="right">{l.debit ? formatMoney(l.debit) : ''}</TableCell>
                                    <TableCell align="right">{l.credit ? formatMoney(l.credit) : ''}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Box>
    );
};

export default ProceedsDistributionGrid;
